import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Modal } from "react-bootstrap";
import { DeleteIconButton } from "components/DatatablesActionIcons";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import type { GroupBase } from "react-select";
import { useLocation } from "react-router-dom";
import { getAuthCookie } from "../utils/authCookies";

type Select2Option = { value: string; label: string };
type AdditionalType = { page?: number };

type LocationRow = {
  id: string;
  warehouse: Select2Option | null;
  zone: Select2Option | null;
  rack: Select2Option | null;
  bin: Select2Option | null;
  max_volume?: number;
  used_volume?: number;
  qty: number;
};

type LocationPayload = {
  quantity: number;
  warehouse_id: string;
  warehouse_name: string;
  zone_id: string;
  zone_name: string;
  rack_id: string;
  rack_name: string;
  bin_id: string;
  bin_name: string;
};

type Props = {
  show: boolean;
  onHide: () => void;
  onConfirm: (locations: LocationPayload[], totalQuantity: number) => void;
  productQuantity?: number;
  readOnly?: boolean;
  /** Warehouse owner-node id (signage userId, or parent signage id for teammember). */
  ownerNodeId?: string;
};

const PAGE_SIZE_LOCATION = 20;

const locationSelectStyles = {
  control: (provided: any, state: any) => ({
    ...provided,
    minHeight: "38px",
    border: state.isFocused ? "1px solid #86b7fe" : "1px solid #ced4da",
    borderRadius: "0.375rem",
    boxShadow: state.isFocused ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)" : "none",
    "&:hover": { borderColor: "#86b7fe" },
  }),
  placeholder: (provided: any) => ({ ...provided, color: "#6c757d" }),
  menu: (provided: any) => ({ ...provided, zIndex: 9999 }),
};

const BinLocationPopUp: React.FC<Props> = ({
  show,
  onHide,
  onConfirm,
  productQuantity = 0,
  readOnly = false,
  ownerNodeId,
}) => {
  const tokenRef = useRef<string>(getAuthCookie("sessionToken") || "");
  const locationPath = useLocation();
  const isSignageTeammember = locationPath.pathname.includes("/signageteammember");
  const cookieUserId = getAuthCookie("userId") || "";
  const [resolvedOwnerNodeId, setResolvedOwnerNodeId] = useState<string>(
    ownerNodeId || (!isSignageTeammember ? cookieUserId : "")
  );

  useEffect(() => {
    if (ownerNodeId) {
      setResolvedOwnerNodeId(ownerNodeId);
      return;
    }
    if (!isSignageTeammember) {
      setResolvedOwnerNodeId(cookieUserId);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/parent-hierarchy`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${tokenRef.current}`,
            "Content-Type": "application/json",
          },
        });
        if (!response.ok || cancelled) return;
        const data = await response.json();
        const signageFirm = data.parent_chain?.find(
          (parent: any) => parent.license_type === "SIGNAGE_FIRM"
        );
        if (!cancelled && signageFirm) {
          setResolvedOwnerNodeId(signageFirm.id || signageFirm._id || "");
        }
      } catch {
        // keep empty
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ownerNodeId, isSignageTeammember, cookieUserId]);

  const mapSelect2Results = useCallback((json: any): { options: Select2Option[]; hasMore: boolean } => {
    const data = json?.data ?? json;
    const results = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
    const options: Select2Option[] = results
      .map((item: any) => ({
        value: String(item.id ?? item._id ?? ""),
        label: String(item.text ?? item.label ?? item.name ?? ""),
      }))
      .filter((o: Select2Option) => o.value);
    const hasMore = Boolean(data?.pagination?.more);
    return { options, hasMore };
  }, []);

  const loadWarehouseOptions: LoadOptions<Select2Option, GroupBase<Select2Option>, AdditionalType> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      const page = additional?.page ?? 1;
      if (!resolvedOwnerNodeId) {
        return { options: [], hasMore: false, additional: { page } };
      }
      try {
        const params = new URLSearchParams({
          search: (inputValue || "").trim(),
          page: String(page),
          limit: String(PAGE_SIZE_LOCATION),
        });
        const url = `${process.env.REACT_APP_API_URL}/warehouses/owner-node/${resolvedOwnerNodeId}/select2?${params}`;
        const response = await fetch(url, {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${tokenRef.current}`,
          },
        });
        if (!response.ok) {
          return { options: [], hasMore: false, additional: { page } };
        }
        const json = await response.json();
        const { options, hasMore } = mapSelect2Results(json);
        return { options, hasMore, additional: { page: page + 1 } };
      } catch {
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [resolvedOwnerNodeId, mapSelect2Results]
  );

  const loadZoneOptions = useCallback(
    (warehouseId: string): LoadOptions<Select2Option, GroupBase<Select2Option>, AdditionalType> =>
      async (inputValue, _loadedOptions, additional) => {
        const page = additional?.page ?? 1;
        if (!warehouseId) {
          return { options: [], hasMore: false, additional: { page } };
        }
        try {
          const params = new URLSearchParams({
            search: (inputValue || "").trim(),
            page: String(page),
            limit: String(PAGE_SIZE_LOCATION),
          });
          const url = `${process.env.REACT_APP_API_URL}/zones/warehouse/${warehouseId}/select2?${params}`;
          const response = await fetch(url, {
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${tokenRef.current}`,
            },
          });
          if (!response.ok) {
            return { options: [], hasMore: false, additional: { page } };
          }
          const json = await response.json();
          const { options, hasMore } = mapSelect2Results(json);
          return { options, hasMore, additional: { page: page + 1 } };
        } catch {
          return { options: [], hasMore: false, additional: { page } };
        }
      },
    [mapSelect2Results]
  );

  const loadRackOptions = useCallback(
    (zoneId: string): LoadOptions<Select2Option, GroupBase<Select2Option>, AdditionalType> =>
      async (inputValue, _loadedOptions, additional) => {
        const page = additional?.page ?? 1;
        if (!zoneId) {
          return { options: [], hasMore: false, additional: { page } };
        }
        try {
          const params = new URLSearchParams({
            search: (inputValue || "").trim(),
            page: String(page),
            limit: String(PAGE_SIZE_LOCATION),
          });
          const url = `${process.env.REACT_APP_API_URL}/racks/zone/${zoneId}/select2?${params}`;
          const response = await fetch(url, {
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${tokenRef.current}`,
            },
          });
          if (!response.ok) {
            return { options: [], hasMore: false, additional: { page } };
          }
          const json = await response.json();
          const { options, hasMore } = mapSelect2Results(json);
          return { options, hasMore, additional: { page: page + 1 } };
        } catch {
          return { options: [], hasMore: false, additional: { page } };
        }
      },
    [mapSelect2Results]
  );

  const loadBinOptions = useCallback(
    (
      rackId: string,
      excludeBinIds: string[] = []
    ): LoadOptions<Select2Option, GroupBase<Select2Option>, AdditionalType> =>
      async (inputValue, _loadedOptions, additional) => {
        const page = additional?.page ?? 1;
        if (!rackId) {
          return { options: [], hasMore: false, additional: { page } };
        }
        try {
          const params = new URLSearchParams({
            search: (inputValue || "").trim(),
            page: String(page),
            limit: String(PAGE_SIZE_LOCATION),
          });
          const url = `${process.env.REACT_APP_API_URL}/bins/rack/${rackId}/select2?${params}`;
          const response = await fetch(url, {
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${tokenRef.current}`,
            },
          });
          if (!response.ok) {
            return { options: [], hasMore: false, additional: { page } };
          }
          const json = await response.json();
          const { options, hasMore } = mapSelect2Results(json);
          const filtered = excludeBinIds.length
            ? options.filter((o) => !excludeBinIds.includes(o.value))
            : options;
          return { options: filtered, hasMore, additional: { page: page + 1 } };
        } catch {
          return { options: [], hasMore: false, additional: { page } };
        }
      },
    [mapSelect2Results]
  );

  const fetchBinVolume = useCallback(async (binId: string) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/bins/${binId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tokenRef.current}`,
        },
      });
      if (!response.ok) {
        return { max_volume: undefined as number | undefined, used_volume: undefined as number | undefined };
      }
      const binData = await response.json();
      return {
        max_volume: binData?.max_volume as number | undefined,
        used_volume: binData?.used_volume as number | undefined,
      };
    } catch {
      return { max_volume: undefined as number | undefined, used_volume: undefined as number | undefined };
    }
  }, []);

  const emptyRow = (): LocationRow => ({
    id: crypto.randomUUID(),
    warehouse: null,
    zone: null,
    rack: null,
    bin: null,
    qty: 0,
  });

  const [rows, setRows] = useState<LocationRow[]>([emptyRow()]);

  const allocated = useMemo(
    () => rows.reduce((s, r) => s + (Number(r.qty) || 0), 0),
    [rows]
  );

  const remainingQuantity = useMemo(
    () => Math.max(0, productQuantity - allocated),
    [productQuantity, allocated]
  );

  const rowToPayload = useCallback((row: LocationRow): LocationPayload | null => {
    if (!row.warehouse || !row.zone || !row.rack || !row.bin || !row.qty) return null;
    return {
      quantity: Number(row.qty || 0),
      warehouse_id: row.warehouse.value,
      warehouse_name: row.warehouse.label,
      zone_id: row.zone.value,
      zone_name: row.zone.label,
      rack_id: row.rack.value,
      rack_name: row.rack.label,
      bin_id: row.bin.value,
      bin_name: row.bin.label,
    };
  }, []);

  const payload = useMemo(
    () => rows.map(rowToPayload).filter(Boolean) as LocationPayload[],
    [rows, rowToPayload]
  );

  const addRow = useCallback(() => setRows((prev) => [...prev, emptyRow()]), []);

  const removeRow = useCallback(
    (id: string) => setRows((prev) => prev.filter((r) => r.id !== id)),
    []
  );

  const handleWarehouseChange = useCallback((id: string, selected: Select2Option | null) => {
    setRows((prev) =>
      prev.map((x) =>
        x.id === id
          ? {
              ...x,
              warehouse: selected,
              zone: null,
              rack: null,
              bin: null,
              max_volume: undefined,
              used_volume: undefined,
              qty: 0,
            }
          : x
      )
    );
  }, []);

  const handleZoneChange = useCallback((id: string, selected: Select2Option | null) => {
    setRows((prev) =>
      prev.map((x) =>
        x.id === id
          ? {
              ...x,
              zone: selected,
              rack: null,
              bin: null,
              max_volume: undefined,
              used_volume: undefined,
              qty: 0,
            }
          : x
      )
    );
  }, []);

  const handleRackChange = useCallback((id: string, selected: Select2Option | null) => {
    setRows((prev) =>
      prev.map((x) =>
        x.id === id
          ? {
              ...x,
              rack: selected,
              bin: null,
              max_volume: undefined,
              used_volume: undefined,
              qty: 0,
            }
          : x
      )
    );
  }, []);

  const handleBinChange = useCallback(
    async (id: string, selected: Select2Option | null) => {
      setRows((prev) =>
        prev.map((x) =>
          x.id === id
            ? {
                ...x,
                bin: selected,
                max_volume: undefined,
                used_volume: undefined,
                qty: 0,
              }
            : x
        )
      );
      if (!selected?.value) return;
      const vol = await fetchBinVolume(selected.value);
      setRows((prev) =>
        prev.map((x) =>
          x.id === id && x.bin?.value === selected.value
            ? { ...x, max_volume: vol.max_volume, used_volume: vol.used_volume }
            : x
        )
      );
    },
    [fetchBinVolume]
  );

  const [showModal, setShowModal] = useState(false);
  const [modalText, setModalText] = useState("");
  const [modalHeader, setModalHeader] = useState("Validation Error");

  const updateQty = useCallback(
    (id: string, val: string) => {
      const next = Math.max(0, Number(val || 0));

      const currentRow = rows.find((r) => r.id === id);
      if (!currentRow) return;

      const currentAllocated = rows.reduce((s, r) => {
        if (r.id === id) return s;
        return s + (Number(r.qty) || 0);
      }, 0);
      const newTotalAllocated = currentAllocated + next;

      if (productQuantity > 0 && newTotalAllocated > productQuantity) {
        setModalHeader("Quantity Allocation Error");
        setModalText(`You can only allocate up to ${productQuantity} units.`);
        setShowModal(true);
        return;
      }

      if (currentRow.bin && currentRow.max_volume != null) {
        const maxVolume = currentRow.max_volume || 0;
        const usedVolume = currentRow.used_volume || 0;
        const availableVolume = maxVolume - usedVolume;
        if (next > availableVolume) {
          setModalHeader("Insufficient Bin Space");
          setModalText("No available space in this bin for the selected quantity. Please choose another bin.");
          setShowModal(true);
          return;
        }
      }

      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, qty: next } : r)));
    },
    [rows, productQuantity]
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (showModal && event.key === "Enter") {
        event.preventDefault();
        setShowModal(false);
      }
    };

    if (showModal) {
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, [showModal]);

  useEffect(() => {
    if (!show) {
      setRows([emptyRow()]);
    }
  }, [show]);

  const handleConfirm = () => {
    if (payload.length === 0) {
      setModalHeader("Validation Error");
      setModalText("Please add at least one location with quantity.");
      setShowModal(true);
      return;
    }

    if (productQuantity > 0 && allocated > productQuantity) {
      setModalHeader("Quantity Allocation Error");
      setModalText(`You can only allocate up to ${productQuantity} units.`);
      setShowModal(true);
      return;
    }

    onConfirm(payload, allocated);
    onHide();
  };

  const canSave = useMemo(() => {
    if (readOnly || payload.length === 0) return false;
    if (productQuantity > 0) {
      return remainingQuantity === 0;
    }
    return true;
  }, [readOnly, payload.length, productQuantity, remainingQuantity]);

  return (
    <>
      <style>
        {`
          .bin-location-modal .modal-content {
            max-height: 85vh;
            display: flex;
            flex-direction: column;
            left: 80px !important;
          }
          .error-modal .modal-content {
            left: 5.5rem !important;
          }
          .bin-location-modal .modal-header {
            flex-shrink: 0;
            padding: 0;
            border-bottom: 1px solid #dee2e6;
            display: block !important;
          }
          .bin-location-modal .modal-header .header-content {
            padding: 1rem 1.5rem;
            padding-right: 3rem;
          }
          .bin-location-modal .modal-header .header-top-row {
            display: flex;
            align-items: center;
            margin-bottom: 2rem;
          }
          .bin-location-modal .modal-header .handover-badge {
            background: #eff6ff;
            color: #155dfc;
            padding: 0.6rem 0.9rem;
            font-size: 1rem;
            font-weight: 500;
            white-space: nowrap;
            border: 1px solid #bedbff;
          }
          .bin-location-modal .modal-header .header-bottom-row {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 1rem;
          }
          .bin-location-modal .modal-header .modal-title {
            margin: 0;
            font-size: 1.25rem;
            font-weight: 600;
          }
          .bin-location-modal .modal-header .add-location-btn {
            font-size: 0.875rem;
            padding: 0.375rem 0.75rem;
          }
          .bin-location-modal .modal-header .btn-close {
            position: absolute;
            top: 1rem;
            right: 1rem;
            z-index: 1;
          }
          .bin-location-modal .modal-body {
            flex: 1 1 auto;
            overflow-y: scroll !important;
            overflow-x: hidden;
            max-height: 70vh;
            padding: 1.5rem;
          }
          .bin-location-modal .modal-footer {
            flex-shrink: 0;
            border-top: 1px solid #dee2e6;
            padding: 1rem 1.5rem;
            display: flex;
            justify-content: space-between;
            align-items: center;
            background: #fbf9fa;
            border-radius: 0px 0px 23px 23px;
          }
          .bin-location-modal .summary-box {
            background: #f8f9fa;
            border: 1px solid #dee2e6;
            border-radius: 0.5rem;
            padding: 1rem 1.25rem;
            margin-bottom: 1.5rem;
          }
          .bin-location-modal .location-card {
            border: 1px solid #e9edf7;
            border-radius: 0.75rem;
            background: #fff;
            padding: 1rem;
            margin-bottom: 1rem;
          }
          .bin-location-modal .location-card .card-header-section {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 1rem;
            padding-bottom: 0.75rem;
            border-bottom: 1px solid #e9edf7;
          }
          .bin-location-modal .location-card .location-title {
            color: #101828;
            font-weight: 500;
            font-size: 0.95rem;
          }
          .bin-location-modal .location-card .form-group {
            margin-bottom: 1rem;
          }
          .bin-location-modal .location-card .form-group label {
            display: block;
            color: #101828;
            font-size: 0.875rem;
            font-weight: 500;
            margin-bottom: 0.5rem;
          }
          .bin-location-modal .location-card .quantity-row {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            gap: 1rem;
          }
          @media (max-width: 991.98px) {
            .bin-location-modal .modal-dialog {
              max-width: 95%;
              margin: 0.5rem auto;
            }
            .bin-location-modal .modal-content {
              max-height: 90vh;
              left: 0px !important;
            }
            .error-modal .modal-content {
              left: 0rem !important;
            }
            .bin-location-modal .modal-header .header-content {
              padding: 0.75rem 1rem;
              padding-right: 2.5rem;
            }
            .bin-location-modal .modal-body {
              padding: 1rem;
              max-height: 65vh;
            }
            .bin-location-modal .summary-box {
              padding: 0.875rem 1rem;
              margin-bottom: 1rem;
            }
            .bin-location-modal .modal-footer {
              padding: 0.75rem 1rem;
              flex-direction: column;
              gap: 0.75rem;
            }
            .bin-location-modal .modal-footer .btn {
              width: 100%;
            }
            .bin-location-modal .location-card .quantity-row {
              grid-template-columns: 1fr;
            }
          }
          @media (max-width: 575.98px) {
            .bin-location-modal .modal-dialog {
              max-width: 98%;
              margin: 0.25rem auto;
            }
            .bin-location-modal .modal-content {
              max-height: 95vh;
              left: 0px !important;
            }
            .bin-location-modal .modal-header .header-content {
              padding: 0.5rem 0.75rem;
              padding-right: 2rem;
            }
            .bin-location-modal .modal-body {
              padding: 0.75rem;
              max-height: 60vh;
            }
            .bin-location-modal .location-card {
              padding: 0.875rem;
            }
          }
        `}
      </style>

      <Modal show={show} onHide={onHide} size="xl" centered className="bin-location-modal">
        <Modal.Header closeButton>
          <div className="header-content">
            {productQuantity > 0 && (
              <div className="header-top-row justify-content-center justify-content-lg-start">
                <span className="handover-badge br-8">
                  <span className="text-black">Handover Qty:</span>{" "}
                  <span className="fs-18 fw-bold">{productQuantity}</span>
                </span>
              </div>
            )}
            <div className="header-bottom-row">
              <Modal.Title>Inventory &amp; Locations</Modal.Title>
              <button
                type="button"
                className="btn btn-primary table-addbutton"
                onClick={addRow}
                disabled={readOnly}
              >
                <i className="bi bi-plus-lg me-2" /> Add Location
              </button>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body>
          {productQuantity > 0 && (
            <div className="summary-box col-lg-4">
              <div className="justify-content-between align-items-center">
                <span className="fs-15 fw-medium">Summary</span>
                <div className="mt-2">
                  <span className="fs-14">
                    {allocated} / {productQuantity} handover units allocated
                  </span>
                </div>
                <div className="d-flex align-items-center mt-2 fs-14" style={{ color: "#00a63e" }}>
                  ✓ &nbsp; <span className="fs-14">Remaining quantity available: {remainingQuantity}</span>
                </div>
              </div>
            </div>
          )}

          {rows.map((r, i) => {
            const maxVolume = r.max_volume || 0;
            const usedVolume = r.used_volume || 0;
            const availableVolume = r.max_volume != null ? Math.max(0, maxVolume - usedVolume) : "";

            const selectedBinIds = rows
              .filter((row) => row.id !== r.id && row.bin?.value)
              .map((row) => row.bin!.value);

            return (
              <div key={r.id} className="location-card">
                <div className="card-header-section d-block d-md-flex">
                  <div className="location-title">Warehouse &amp; Bin Location #{i + 1}</div>
                  <DeleteIconButton
                    type="button"
                    className="btn btn-link text-danger p-0"
                    onClick={() => removeRow(r.id)}
                    disabled={readOnly}
                  />
                </div>

                <div className="row g-3">
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Warehouse</label>
                      <AsyncPaginate
                        classNamePrefix="rs"
                        placeholder="Search warehouse..."
                        isDisabled={readOnly || !resolvedOwnerNodeId}
                        value={r.warehouse}
                        loadOptions={loadWarehouseOptions}
                        onChange={(selected: Select2Option | null) => handleWarehouseChange(r.id, selected)}
                        additional={{ page: 1 }}
                        defaultOptions
                        isClearable
                        cacheUniqs={[resolvedOwnerNodeId]}
                        styles={locationSelectStyles}
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Zone</label>
                      <AsyncPaginate
                        classNamePrefix="rs"
                        placeholder={r.warehouse ? "Search zone..." : "Select warehouse first"}
                        isDisabled={readOnly || !r.warehouse}
                        value={r.zone}
                        loadOptions={loadZoneOptions(r.warehouse?.value || "")}
                        onChange={(selected: Select2Option | null) => handleZoneChange(r.id, selected)}
                        additional={{ page: 1 }}
                        defaultOptions={Boolean(r.warehouse)}
                        isClearable
                        cacheUniqs={[r.warehouse?.value]}
                        styles={locationSelectStyles}
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Rack</label>
                      <AsyncPaginate
                        classNamePrefix="rs"
                        placeholder={r.zone ? "Search rack..." : "Select zone first"}
                        isDisabled={readOnly || !r.zone}
                        value={r.rack}
                        loadOptions={loadRackOptions(r.zone?.value || "")}
                        onChange={(selected: Select2Option | null) => handleRackChange(r.id, selected)}
                        additional={{ page: 1 }}
                        defaultOptions={Boolean(r.zone)}
                        isClearable
                        cacheUniqs={[r.zone?.value]}
                        styles={locationSelectStyles}
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Bin</label>
                      <AsyncPaginate
                        classNamePrefix="rs"
                        placeholder={r.rack ? "Search bin..." : "Select rack first"}
                        isDisabled={readOnly || !r.rack}
                        value={r.bin}
                        loadOptions={loadBinOptions(r.rack?.value || "", selectedBinIds)}
                        onChange={(selected: Select2Option | null) => handleBinChange(r.id, selected)}
                        additional={{ page: 1 }}
                        defaultOptions={Boolean(r.rack)}
                        isClearable
                        cacheUniqs={[r.rack?.value, selectedBinIds.join(",")]}
                        styles={locationSelectStyles}
                      />
                    </div>
                  </div>
                </div>

                <div className="quantity-row mt-1">
                  <div className="form-group">
                    <label>Available Quantity</label>
                    <input
                      type="number"
                      className="form-control br-8"
                      value={availableVolume}
                      readOnly
                      disabled
                      style={{ backgroundColor: "#f8f9fa" }}
                    />
                  </div>
                  <div className="form-group">
                    <label>Maximum Quantity</label>
                    <input
                      type="number"
                      className="form-control br-8"
                      value={r.max_volume != null ? maxVolume : ""}
                      readOnly
                      disabled
                      style={{ backgroundColor: "#f8f9fa" }}
                    />
                  </div>
                  <div className="form-group">
                    <label>Quantity</label>
                    <input
                      type="number"
                      className="form-control br-8"
                      min={0}
                      value={r.qty === 0 ? "" : r.qty}
                      placeholder="0"
                      onChange={(e) => updateQty(r.id, e.target.value)}
                      disabled={readOnly || !r.bin}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </Modal.Body>

        <Modal.Footer className="py-2">
          <button type="button" onClick={onHide} className="btn back-btn surface-btn fw-bold fs-16">
            <i className="bi bi-chevron-left me-2" />
            Back
          </button>
          <button
            type="button"
            className="btn btn-primary table-addbutton"
            onClick={handleConfirm}
            disabled={!canSave}
          >
            Save
          </button>
        </Modal.Footer>
      </Modal>

      {showModal && (
        <div
          className="error-modal modal fade show"
          style={{ display: "block", background: "rgba(0,0,0,0.35)", zIndex: 1060 }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body p-4">
                <div className="d-flex align-items-start">
                  <div className="me-2 text-danger fs-4">
                    <i className="bi bi-exclamation-circle" />
                  </div>
                  <div>
                    <h5 className="mb-2">{modalHeader}</h5>
                    <div>{modalText}</div>
                  </div>
                </div>
                <div className="text-end mt-3">
                  <button type="button" className="btn btn-primary" onClick={() => setShowModal(false)}>
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BinLocationPopUp;
